import { computed, onMounted, reactive, ref } from "vue";
import constants from "@/utils/constants";
import { fromServer } from "@/utils/translations";
import router from "@/router";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { validations as validationRules } from "leatherman-js";

export const loginMixin = {
  props: {
    forgotPasswordAction: {
      type: Function,
      default: () => {
        router.push({ name: constants.ROUTES.FORGOT_PASSWORD });
      },
    },
    signupAction: {
      type: Function,
      default: () => {
        router.push({ name: constants.ROUTES.SIGNUP });
      },
    },
  },
};

export const loginSetup = (props, { emit }) => {
  // display variables
  const loading = ref(false);
  const hasCell = ref(true);
  const errors = ref(null);
  const recaptchaBypass = ref(null);

  // login variables and validation
  const loginFields = reactive({
    email: process.env.VUE_APP_DEFAULT_EMAIL,
    password: process.env.VUE_APP_DEFAULT_PASSWORD,
    twoFactorRequired: false,
    twoFactorCode: null,
    rememberMe: false,
    recaptchaToken: null,
  });
  const { validator } = useValidator();
  const validation = validator(
    loginFields,
    validationRules.forms.login,
    {},
    {
      email: {
        required: "Email address is required",
      },
      password: {
        required: "Password is required",
      },
      twoFactorCode: {
        regex: "Invalid Authentication Code",
        requiredIf: "Authentication code is required",
      },
    }
  );

  //recaptcha variables and methods
  const recaptchaType = ref(process.env.VUE_APP_RECAPTCHA_TYPE);
  const recaptchaSiteKey = ref(process.env.VUE_APP_RECAPTCHA_SITE_KEY);
  const recaptchaContainer = ref(null);
  const recaptchaContainerInvisible = ref(null);
  const useRecaptcha = computed(() => store.state.settings.loginRecaptchaEnabled);

  const formSubmit = (token) => {
    if (recaptchaType.value === "invisible") {
      onVerify(token);
    }

    submit();
  };

  const onVerify = (token) => (loginFields.recaptchaToken = token);

  const onExpired = () => {
    resetRecaptcha();
  };

  const onForgotPasswordClick = () => {
    props.forgotPasswordAction();
    emit("close");
  };

  const onSignUpClick = () => {
    props.signupAction();
    emit("close");
  };

  const resetRecaptcha = () => {
    loginFields.recaptchaToken = null;
    if (recaptchaContainer.value) {
      recaptchaContainer.value.reset();
    }
    if (recaptchaContainerInvisible.value) {
      recaptchaContainerInvisible.value.reset();
    }
  };
  const company = computed(() => store.getters["company/get"]);

  // submit the form
  const submit = () => {
    errors.value = null;

    validation
      .validate()
      .then(() => {
        loading.value = true;

        const data = {
          email: loginFields.email,
          password: loginFields.password,
          recaptchaToken: loginFields.recaptchaToken,
          twoFactorCode: loginFields.twoFactorCode,
          rememberMe: loginFields.rememberMe,
        };
        if (recaptchaBypass.value) {
          data.recaptchaBypass = recaptchaBypass.value;
        }

        store
          .dispatch("auth/login", data)
          .then(async () => {
            store.dispatch("cartstack/setEmail", loginFields.email);
            await store.dispatch("auth/userClientInfo", { reidentify: true, lazy: true });
            emit("success");
          })
          .catch((e) => {
            // if two factor is required, show the two factor code field
            if (e.twoFactorRequired) {
              loginFields.twoFactorRequired = true;
              // if the user has a cell number available for two factor, show the sms button
              hasCell.value = !!e.hasCell;
              validation.reset();
              if (recaptchaType.value === "invisible") {
                resetRecaptcha();
              }
            } else {
              resetRecaptcha();
              errors.value = fromServer(e.request.response);
            }
          })
          .finally(() => {
            loading.value = false;
          });
      })
      .catch(() => resetRecaptcha());
  };

  const smsLoading = ref(false);
  const smsSent = ref(false);
  const send2faSMS = () => {
    smsLoading.value = true;
    store
      .dispatch("auth/send2faSMS", loginFields.email)
      .then(() => {
        smsSent.value = true;
      })
      .finally(() => (smsLoading.value = false));
  };

  return {
    errors,
    loginFields,
    loading,
    onExpired,
    onVerify,
    recaptchaSiteKey,
    recaptchaContainer,
    recaptchaContainerInvisible,
    resetRecaptcha,
    submit,
    useRecaptcha,
    recaptchaBypass,
    recaptchaType,
    validation,
    hasCell,
    company,
    smsLoading,
    smsSent,
    send2faSMS,
    formSubmit,
    onForgotPasswordClick,
    onSignUpClick,
  };
};

export const signupMixin = {
  props: {
    loginAction: {
      type: Function,
      default: () => {
        router.push({ name: constants.ROUTES.LOGIN });
      },
    },
    email: String,
  },
};

export const signupSetup = (props, { emit }) => {
  // display variables
  const loading = ref(false);
  const errors = ref(null);
  const recaptchaBypass = ref(null);

  const gdpr = computed(() => store.state.auth.gdpr);
  const { firstName, lastName, email } = router.currentRoute.value.query;
  const passwordStrength = ref(0);

  // signup variables and validation
  const signupFields = reactive({
    email: email ? decodeURI(email) : props.email,
    password: null,
    lastName: lastName ? decodeURI(lastName) : null,
    firstName: firstName ? decodeURI(firstName) : null,
    recaptchaToken: null,
    acceptTerms: false,
    longform: props.longform,
    marketingEmails: false,
  });
  const { validator } = useValidator();
  const validation = validator(
    signupFields,
    validationRules.forms.signup,
    {},
    {
      firstName: {
        required: "First name is required",
      },
      lastName: {
        required: "Last name is required",
      },
      email: {
        required: "Email address is required",
        email: "Invalid Email",
      },
      password: {
        required: "Password is required",
      },
      acceptTerms: {
        equal: "You must accept the terms of service to signup.",
      },
    }
  );
  //recaptcha variables and methods
  const recaptchaType = ref(process.env.VUE_APP_RECAPTCHA_TYPE);
  const recaptchaSiteKey = ref(process.env.VUE_APP_RECAPTCHA_SITE_KEY);
  const recaptchaContainer = ref(null);
  const recaptchaContainerInvisible = ref(null);
  const useRecaptcha = computed(() => {
    return store.state.settings.signUpRecaptchaEnabled;
  });

  const formSubmit = (token) => {
    if (recaptchaType.value === "invisible") {
      onVerify(token);
    }

    submit();
  };

  const onVerify = (token) => {
    signupFields.recaptchaToken = token;
  };

  const onExpired = () => {
    resetRecaptcha();
  };

  const resetRecaptcha = () => {
    signupFields.recaptchaToken = null;
    if (recaptchaContainer.value) {
      recaptchaContainer.value.reset();
    }
    if (recaptchaContainerInvisible.value) {
      recaptchaContainerInvisible.value.reset();
    }
  };

  const company = computed(() => store.getters["company/get"]);
  const submittable = computed(
    () => !loading.value && !validation.errors.length && passwordStrength.value >= 3
  );

  // submit the form
  const submit = () => {
    errors.value = null;

    validation
      .validate()
      .then(() => {
        loading.value = true;
        const data = {
          email: signupFields.email,
          firstName: signupFields.firstName,
          lastName: signupFields.lastName,
          password: signupFields.password,
          recaptchaToken: signupFields.recaptchaToken,
          acceptTerms: signupFields.acceptTerms,
          marketingEmails: signupFields.marketingEmails,
        };
        if (recaptchaBypass.value) {
          data.recaptchaBypass = recaptchaBypass.value;
        }

        store
          .dispatch("auth/signup", data)
          .then(() => {
            emit("success");
            store.dispatch("auth/userClientInfo", { reidentify: true });
          })
          .catch((e) => {
            resetRecaptcha();
            if (e.request.response?.email === "E-mail exists") {
              errors.value = "An account with that email address already exists. Please log in.";
            } else {
              errors.value = fromServer(e.request.response);
            }
          })
          .finally(() => {
            loading.value = false;
          });
      })
      .catch(() => {
        resetRecaptcha();
      });
  };

  const emailChanged = () => {
    store.dispatch("cartstack/setEmail", signupFields.email);
  };

  onMounted(() => {
    if (signupFields.email) {
      store.dispatch("cartstack/setEmail", signupFields.email);
    }
  });

  return {
    company,
    emailChanged,
    errors,
    formSubmit,
    gdpr,
    loading,
    onExpired,
    onVerify,
    passwordStrength,
    recaptchaBypass,
    recaptchaContainer,
    recaptchaContainerInvisible,
    recaptchaSiteKey,
    recaptchaType,
    resetRecaptcha,
    signupFields,
    submit,
    submittable,
    useRecaptcha,
    validation,
  };
};
